import {
  AddressSchema,
  BeCoolDepotCode,
  Brand,
  JobStatus,
  OrderAvailableActionsSchema,
  PaymentStatus,
  ProductType,
} from '@lyka-pet-food/contracts'
import { z } from 'zod'
import { OrderDisplayType, OrderPaymentMethodSchema, OrderPaymentSummaryCategorySchema } from '../order'

export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected a calendar date in YYYY-MM-DD')

export const IsoUtcSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, 'expected a UTC instant with a Z suffix')

export const DeliverySlotSchema = z.object({
  id: z.enum(['AM', 'PM']).nullable(),
  label: z.string().nullable(),
})

export const DeliveryPouchBreakdownEntrySchema = z.object({
  weightGrams: z.number(),
  quantity: z.number(),
})

export const DeliveryPouchesSchema = z.object({
  summary: z.string(),
  breakdown: z.array(DeliveryPouchBreakdownEntrySchema),
})

export const DeliveryMealSchema = z.object({
  /** The catalog product id */
  productId: z.string(),
  /** The order line item's variant id. */
  productVariantId: z.string(),
  name: z.string(),
  thumbnail: z.string().nullable(),
  quantity: z.number(),
  sortIndex: z.number(),
})

export const DeliveryAnimalSchema = z.object({
  animalId: z.string(),
  animalName: z.string().nullable(),
  planType: z.string(),
  dailyServingSizeInGrams: z.number().nullable(),
  pouches: DeliveryPouchesSchema,
  meals: z.array(DeliveryMealSchema),
})

export const DeliveryExtraSchema = z.object({
  /** The order line item's variant id */
  productVariantId: z.string(),
  /** The catalog product id owning the variant. */
  productId: z.string(),
  productType: z.enum([ProductType.TREAT, ProductType.SUPPLEMENT]),
  name: z.string(),
  sku: z.string().nullable(),
  thumbnail: z.string().nullable(),
  quantity: z.number(),
  oneOff: z.boolean(),
  /** The active subscription's cadence for this variant; null for one-offs and unmatched variants. */
  cadenceNthOrder: z.number().nullable(),
  total: z.number().nullable(),
  discountedTotal: z.number().nullable(),
})

export const DeliveryPaymentSummaryRowSchema = z.object({
  productVariantId: z.string(),
  name: z.string(),
  quantity: z.number(),
  total: z.number().nullable(),
  discountedTotal: z.number().nullable(),
})

export const DeliveryPaymentSummarySchema = z.object({
  fullBowlPlan: OrderPaymentSummaryCategorySchema.extend({ animalNames: z.array(z.string()) }),
  treats: OrderPaymentSummaryCategorySchema.extend({ rows: z.array(DeliveryPaymentSummaryRowSchema) }),
  supplements: OrderPaymentSummaryCategorySchema.extend({ rows: z.array(DeliveryPaymentSummaryRowSchema) }),
  total: OrderPaymentSummaryCategorySchema,
  paymentMethod: OrderPaymentMethodSchema.optional(),
})

export const DeliveryAddressSchema = AddressSchema.extend({
  createdTimeUTC: IsoUtcSchema.optional(),
  updatedTimeUTC: IsoUtcSchema.optional(),
})

export const DeliverySchema = z.object({
  id: z.string(),
  orderNumber: z.number(),
  status: z.nativeEnum(OrderDisplayType),
  /** Calendar date for the day the box arrives, in `timezone`. */
  deliveryDate: DateOnlySchema,
  /** Calendar date for the day the order is charged, in `timezone`. */
  billingDate: DateOnlySchema,
  billedDate: IsoUtcSchema.nullable(),
  isDeliveryToday: z.boolean(),
  isPaymentToday: z.boolean(),
  daysUntilDelivery: z.number().int(),
  daysUntilPayment: z.number().int(),
  editCutoffAt: IsoUtcSchema,
  deliverySlot: DeliverySlotSchema,
  address: DeliveryAddressSchema,
  deliveryNote: z.string().nullable(),
  timezone: z.string(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  fulfilmentStatus: z.nativeEnum(JobStatus),
  brand: z.nativeEnum(Brand),
  depotCode: BeCoolDepotCode,
  subscriptionId: z.string().nullable(),
  subscriptionOrderIndex: z.number().nullable(),
  availableActions: OrderAvailableActionsSchema.optional(),
  isLocked: z.boolean().optional(),
  isMultiTreatDiscountApplied: z.boolean(),
  animals: z.array(DeliveryAnimalSchema),
  extras: z.array(DeliveryExtraSchema),
  paymentSummary: DeliveryPaymentSummarySchema,
})

export const GetDeliveriesResponseSchema = z.object({
  /** Sorted ascending by the delivery instant then id. HIDDEN excluded, FAILED included. */
  deliveries: z.array(DeliverySchema),
  nextAvailableOrderDate: DateOnlySchema.nullable(),
  nextAvailableOrderBillingDate: DateOnlySchema.nullable(),
  nextAvailableOrderSlot: z.string().nullable(),
})

export type DateOnly = z.infer<typeof DateOnlySchema>
export type IsoUtc = z.infer<typeof IsoUtcSchema>
export type DeliverySlot = z.infer<typeof DeliverySlotSchema>
export type DeliveryPouchBreakdownEntry = z.infer<typeof DeliveryPouchBreakdownEntrySchema>
export type DeliveryPouches = z.infer<typeof DeliveryPouchesSchema>
export type DeliveryMeal = z.infer<typeof DeliveryMealSchema>
export type DeliveryAnimal = z.infer<typeof DeliveryAnimalSchema>
export type DeliveryExtra = z.infer<typeof DeliveryExtraSchema>
export type DeliveryPaymentSummaryRow = z.infer<typeof DeliveryPaymentSummaryRowSchema>
export type DeliveryPaymentSummary = z.infer<typeof DeliveryPaymentSummarySchema>
export type DeliveryAddress = z.infer<typeof DeliveryAddressSchema>
export type Delivery = z.infer<typeof DeliverySchema>
export type GetDeliveriesResponse = z.infer<typeof GetDeliveriesResponseSchema>
